import { useMemo, useState } from 'react';

import { Car, Clock, Link2, Users } from 'lucide-react';

import { formatarData } from '@/nucleo/formato/datas';
import { Badge } from '@/ui/base/badge';
import { CopyableText } from '@/ui/CopyableText';
import { DataTable } from '@/ui/DataTable';
import { ErrorState } from '@/ui/ErrorState';
import { FilterBar, type FilterFieldConfig } from '@/ui/FilterBar';
import { PageHeader } from '@/ui/PageHeader';
import { StatsGrid, type StatItem } from '@/ui/StatsGrid';
import { StatusBadge } from '@/ui/StatusBadge';
import type { TableViewColumn } from '@/ui/table-view/tabelaVisoes';
import { useTableViewPreferences } from '@/ui/table-view/useTableViewPreferences';

import { GatingBanner } from '../comum/GatingBanner';
import { PortalAccessGuard } from '../comum/PortalAccessGuard';
import { useCooperados } from './dados';
import type { Cooperado, FiltroDeCooperados, SituacaoDoCooperado } from './contrato';

const OPCOES_DE_SITUACAO: { value: SituacaoDoCooperado; label: string }[] = [
  { value: 'ativo', label: 'Ativo' },
  { value: 'pendente', label: 'Pendente' },
  { value: 'inativo', label: 'Inativo' },
];

function mascararDocumento(documento: string): string {
  if (documento.length === 11) {
    return documento.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  }
  if (documento.length === 14) {
    return documento.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  }
  return documento;
}

/**
 * `/credenciado/cooperados` — pessoas vinculadas à cooperativa credenciada.
 *
 * Listagem sem detalhe: cada linha já mostra tudo que a cooperativa precisa
 * ver no dia a dia — documento, veículo(s) e situação do vínculo. Dado
 * mockado — ver `dados.ts` e `../comum/mock.ts` para a justificativa.
 * Exclusiva de Cooperativa, pela guarda `requireCoop`, igual ao protótipo.
 */
export function CooperadosPage() {
  return (
    <PortalAccessGuard regra="requireCoop">
      <ListaDeCooperados />
    </PortalAccessGuard>
  );
}

/**
 * Módulo, e não dentro da tela: `useTableViewPreferences` pede lista estável.
 * O nome identifica a linha e não se oculta. A Situação é a coluna de situação
 * do padrão: fica presa no fim do bloco de dados e explica o filtro de mesmo
 * nome.
 */
const COLUNAS: TableViewColumn<Cooperado>[] = [
  {
    id: 'nome',
    header: 'Cooperado',
    cell: (cooperado) => <span className="font-medium">{cooperado.nome}</span>,
    sortKey: 'nome',
    isCritical: true,
  },
  {
    id: 'documento',
    header: 'CPF/CNPJ',
    cell: (cooperado) => (
      <CopyableText
        value={mascararDocumento(cooperado.cpf_cnpj)}
        label={`Copiar documento de ${cooperado.nome}`}
      />
    ),
  },
  {
    id: 'protocolo',
    header: 'Protocolo',
    cell: (cooperado) => (
      <CopyableText value={cooperado.protocolo} label={`Copiar protocolo de ${cooperado.nome}`} />
    ),
  },
  {
    id: 'veiculos',
    header: 'Veículo(s)',
    cell: (cooperado) =>
      cooperado.veiculos.length === 0 ? (
        <span className="text-muted-foreground text-sm">—</span>
      ) : (
        <div className="flex flex-wrap gap-1">
          {cooperado.veiculos.map((placa) => (
            <Badge key={placa} variant="outline" className="font-mono text-[11px]">
              {placa}
            </Badge>
          ))}
        </div>
      ),
  },
  {
    id: 'situacao',
    header: 'Situação',
    cell: (cooperado) => <StatusBadge status={cooperado.situacao} label={cooperado.situacao} />,
    sortKey: 'situacao',
    linkedFilterKeys: ['situacao'],
  },
  {
    id: 'status-inscricao',
    header: 'Status inscrição',
    cell: (cooperado) => (
      <Badge variant="secondary" className="font-normal">
        {cooperado.statusInscricao}
      </Badge>
    ),
  },
  {
    id: 'origem',
    header: 'Origem',
    cell: (cooperado) => (
      <span className="text-sm">
        {cooperado.deTerceiro ? 'Indicado pela cooperativa' : 'Auto-indicação'}
      </span>
    ),
  },
  {
    id: 'vinculado-em',
    header: 'Vinculado em',
    cell: (cooperado) => <span className="text-sm">{formatarData(cooperado.vinculadoEm)}</span>,
    sortKey: 'vinculadoEm',
  },
];

function ListaDeCooperados() {
  const [busca, setBusca] = useState('');
  const [situacao, setSituacao] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const filtro: FiltroDeCooperados = useMemo(
    () => ({
      pagina: page,
      tamanho: perPage,
      busca: busca || undefined,
      situacao: (situacao || undefined) as SituacaoDoCooperado | undefined,
    }),
    [page, perPage, busca, situacao],
  );

  const { data, isLoading, isError, refetch } = useCooperados(filtro);

  const temFiltro = Boolean(busca || situacao);

  const campos: FilterFieldConfig[] = [
    {
      key: 'busca',
      label: 'Busca',
      type: 'search',
      placeholder: 'Buscar por nome ou CPF/CNPJ…',
      ariaLabel: 'Buscar cooperados',
      flex: true,
    },
    {
      key: 'situacao',
      label: 'Situação',
      type: 'select',
      options: OPCOES_DE_SITUACAO,
      width: 'w-full sm:w-[160px]',
    },
  ];

  // Os três primeiros contam o que está na página mostrada (a lista é paginada
  // no servidor e não há resumo da carteira inteira); só o total vale para o
  // conjunto filtrado inteiro. Os rótulos dizem isso, para o indicador não ser
  // lido como total da carteira.
  const stats: StatItem[] = data
    ? [
        {
          title: 'Ativos na página',
          value: data.itens.filter((cooperado) => cooperado.situacao === 'ativo').length,
          icon: Users,
        },
        {
          title: 'Pendentes na página',
          value: data.itens.filter((cooperado) => cooperado.situacao === 'pendente').length,
          icon: Clock,
        },
        {
          title: 'Veículos na página',
          value: data.itens.reduce((total, cooperado) => total + cooperado.veiculos.length, 0),
          icon: Car,
        },
        {
          title: temFiltro ? 'Vínculos no filtro' : 'Total de vínculos',
          value: data.total,
          icon: Link2,
        },
      ]
    : [];

  const filterValues = useMemo(() => ({ situacao }), [situacao]);

  const tableView = useTableViewPreferences({
    contextKey: 'credenciado:cooperados:list',
    columns: COLUNAS,
    filterValues,
  });

  return (
    <div className="space-y-4">
      <PageHeader
        title="Meus cooperados"
        description="Pessoas vinculadas à sua cooperativa no Sistema TEG."
      />

      <GatingBanner />

      {data && data.itens.length > 0 && (
        <StatsGrid stats={stats} columns="grid-cols-1 sm:grid-cols-2 lg:grid-cols-4" />
      )}

      <FilterBar
        fields={campos}
        values={{ busca, situacao }}
        onChange={(chave, valor) => {
          if (chave === 'busca') setBusca(valor);
          if (chave === 'situacao') setSituacao(valor);
          setPage(1);
        }}
        onClear={() => {
          setBusca('');
          setSituacao('');
          setPage(1);
        }}
      />

      {isError ? (
        <ErrorState
          variant="load-failed"
          description="Não foi possível carregar seus cooperados agora. Tente novamente em instantes."
          primaryAction={{ label: 'Tentar de novo', onClick: () => void refetch() }}
        />
      ) : (
        <DataTable
          columns={tableView.orderedColumns}
          personalizacao={tableView}
          data={data?.itens ?? []}
          isLoading={isLoading}
          total={data?.total}
          page={page}
          perPage={perPage}
          onPageChange={setPage}
          onPerPageChange={(novoPerPage) => {
            setPerPage(novoPerPage);
            setPage(1);
          }}
          emptyIcon={Users}
          emptyTitle="Nenhum cooperado vinculado"
          emptyDescription={
            temFiltro
              ? 'Nenhum cooperado atende a esses filtros. Tente afrouxar a busca.'
              : 'Quando houver pessoa vinculada à sua cooperativa, ela aparece aqui.'
          }
          minWidth="960px"
          ariaLabel="Lista de cooperados"
        />
      )}
    </div>
  );
}

export default CooperadosPage;
